import { isString } from 'lodash';
import * as React from 'react';
import cx from 'classnames';

import Button from 'components/Button';
import Icon, { IconName } from 'components/Icon';
// @ts-ignore
import LayerTrigger from 'components/LayerTrigger';
import Text from 'components/Text';
import Tooltip from 'components/Tooltip';

import './styles/TooltipHovercardIconButton.scss';

export type Props = {
  cardPlacement: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  dataTestSelector?: string;
  disabled?: boolean;
  eventKey?: string;
  eventProperties?: { [key: string]: any };
  icon: IconName;
  layer: React.ReactElement;
  switchedOn?: boolean;
  tooltipPlacement: 'top' | 'bottom' | 'left' | 'right';
  tooltipText: string;
};

export type State = {
  hovercardOpen: boolean;
};

/* An icon button that shows a tooltip on hover and toggles a hovercard layer on click.
 * When |disabled|, it still shows the tooltip, but doesn't open the hovercard.
 * The |switchedOn| flag styles the button in brand colors instead of greys, and is meant
 * to indicate that some effect of the button is currently active.
 *
 * Currently only supports a single button type, 32px square with an 18px icon. Supporting
 * arbitrary buttons is possible but tricky to achieve with nested LayerTriggers due to the
 * following:
 * 1. layer positioning seems to break on rerender unless the manually-controlled tooltip
 *    LayerTrigger is nested inside the button (probably something to do with the ref)
 * 2. when the tooltip LayerTrigger is nested inside the button, the tooltip layer is positioned
 *    according to the button's content rather than the button itself, which looks bad for any
 *    button with padding.
 */
class TooltipHovercardIconButton extends React.Component<Props, State> {
  static defaultProps = { cardPlacement: 'bottom', tooltipPlacement: 'top' };

  tooltipLayerTriggerRef: React.RefObject<LayerTrigger>;

  constructor(props: Props) {
    super(props);
    this.state = { hovercardOpen: false };
    this.tooltipLayerTriggerRef = React.createRef<LayerTrigger>();
  }

  handleButtonEnter = () => {
    if (!this.state.hovercardOpen) {
      this.tooltipLayerTriggerRef.current?.showLayer();
    }
  };

  handleButtonLeave = (event: React.MouseEvent) => {
    const button = event.currentTarget;
    if (button instanceof HTMLElement) {
      button.blur();
    }
    this.tooltipLayerTriggerRef.current?.hideLayer();
  };

  handleHovercardShow = () => {
    this.setState({ hovercardOpen: true });
  };

  handleHovercardHide = () => {
    this.setState({ hovercardOpen: false });
  };

  /**
   * There is a LOT of weirdness trying to get multiple <LayerTrigger>s to work together, but
   * that's what we needed here in order to show both a tooltip and hovercard.
   *
   * The hovercard layer is triggered conventionally by click, but the tooltip layer is triggered
   * manually via the |handleButtonEnter| and |handleButtonLeave| functions. These use the
   * |tooltipLayerTriggerRef| to manage the showing and hiding of the tooltip layer.
   *
   * The |hovercardOpen| state is managed by |handleHovercardShow| and |handleHovercardHide|,
   * and allows us to maintain a gray background on the button when the hovercard is open.
   *
   * */

  renderButtonWithTooltip = () => {
    const {
      cardPlacement,
      className,
      dataTestSelector,
      eventKey,
      eventProperties,
      icon,
      layer,
      switchedOn,
      tooltipText,
      tooltipPlacement,
    } = this.props;
    const { hovercardOpen } = this.state;

    return (
      <LayerTrigger
        trigger="click"
        placement={cardPlacement}
        layer={layer}
        onShow={this.handleHovercardShow}
        onHide={this.handleHovercardHide}>
        <Button
          className={cx(
            className,
            'TooltipHovercardIconButton',
            switchedOn && 'TooltipHovercardIconButton-buttonSwitchedOn',
            !switchedOn && 'TooltipHovercardIconButton-hovercardSwitchedOff',
            hovercardOpen && 'TooltipHovercardIconButton-hovercardOpen'
          )}
          level="icon"
          onMouseEnter={this.handleButtonEnter}
          onMouseLeave={this.handleButtonLeave}
          eventKey={eventKey}
          eventProperties={eventProperties}
          data-test-selector={dataTestSelector}>
          <LayerTrigger
            ref={this.tooltipLayerTriggerRef}
            trigger="manual"
            placement={tooltipPlacement}
            layer={<Tooltip>{tooltipText}</Tooltip>}>
            <Text size="xlarge" className="TooltipHovercardIconButton-icon">
              <Icon name={icon} color="black" size="inherit" />
            </Text>
          </LayerTrigger>
        </Button>
      </LayerTrigger>
    );
  };

  render() {
    const { className, disabled, tooltipText, tooltipPlacement, icon } = this.props;

    return disabled ? (
      <Button
        className={cx(
          className,
          'TooltipHovercardIconButton-button',
          'TooltipHovercardIconButton-disabled'
        )}
        level="icon">
        <LayerTrigger
          trigger="hover"
          placement={tooltipPlacement}
          layer={<Tooltip>{tooltipText}</Tooltip>}>
          <Text size="xlarge">
            <Icon name={icon} color="black" size="inherit" />
          </Text>
        </LayerTrigger>
      </Button>
    ) : (
      this.renderButtonWithTooltip()
    );
  }
}

export default TooltipHovercardIconButton;
